import type { LucideIcon } from 'lucide-react';
import { PillBadge } from '@/components/common/PillBadge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { EMPTY_VALUE } from '@/constants/app.constants';

export interface PillField {
  key: string;
  icon: LucideIcon;
  label: string;
  value: string | undefined;
  isLoading: boolean;
  hint?: string | undefined;
}

interface LabeledPillProps {
  field: PillField;
}

function LabeledPill({ field }: LabeledPillProps) {
  if (field.isLoading) return <Skeleton className="h-7 w-40 rounded-lg" />;

  const value = field.value ?? EMPTY_VALUE;
  const pill = (
    <PillBadge icon={field.icon} screenReaderLabel={`${field.label}:`} tone="accent">
      <span className="font-normal text-muted-foreground" aria-hidden>
        {field.label}
      </span>
      <span className="font-semibold text-primary-strong">{value}</span>
    </PillBadge>
  );

  if (!field.hint) return pill;

  return (
    <Tooltip>
      <TooltipTrigger
        render={<span />}
        tabIndex={0}
        aria-label={`${field.label}: ${value}. ${field.hint}`}
        className="rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
      >
        {pill}
      </TooltipTrigger>
      <TooltipContent>{field.hint}</TooltipContent>
    </Tooltip>
  );
}

interface LabeledPillListProps {
  fields: PillField[];
}

export function LabeledPillList({ fields }: LabeledPillListProps) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {fields.map((field) => (
        <LabeledPill key={field.key} field={field} />
      ))}
    </div>
  );
}
